import { Check } from 'lucide-react';
import { useCheckout } from 'modules/jogador/checkout/useCheckout';
import { Card } from 'shared/components/ui/Card';
import { Badge } from 'shared/components/ui/Badge';
import { ProgressBar } from 'shared/components/ui/ProgressBar';
import { PillTabs } from 'shared/components/ui/PillTabs';
import { Button } from 'shared/components/ui/Button';
import { ParticipantRow, ParticipantRowSkeleton } from 'modules/jogador/checkout/components/ParticipantRow';
import { ARENA_NAME, SPORT_LABEL, PAYMENT_METHODS } from 'modules/jogador/checkout/content';
import { formatBRL } from 'shared/utils/format';
import { useToast } from 'shared/components/ui/Toast';
import { copyInviteLink } from 'shared/utils/invite';
import { ROUTES } from 'shared/constants/routes';

export function CheckoutPage() {
  const {
    orderId,
    booking,
    deadline,
    participants,
    isLoadingParticipants,
    quotaCount,
    paidCount,
    totalAmount,
    quotaAmount,
    courtPrice,
    serviceFee,
    paymentMethod,
    setPaymentMethod,
    hasPaid,
    isPaying,
    pay,
  } = useCheckout();

  const toast = useToast();
  const canInvite = booking.splitEnabled && !isLoadingParticipants && participants.length < quotaCount;

  return (
    <section className="mx-auto max-w-2xl px-4 py-8 pb-40">
      <h1 className="font-heading text-2xl font-bold text-ink">Pagamento dividido</h1>
      <p className="mt-1 text-sm text-ink-secondary">Pedido {orderId}</p>

      <Card className="mt-6 border-success/30 bg-success-soft/40">
        <p className="text-sm text-success">
          Vaga bloqueada até {deadline}. Sem todas as cotas pagas, o horário é liberado e o valor
          pago é estornado.
        </p>
      </Card>

      <Card className="mt-4">
        <p className="text-sm text-ink-secondary">
          {ARENA_NAME} · {booking.courtName}
        </p>
        <p className="font-medium text-ink">
          {booking.dateLabel} · {booking.time} · {SPORT_LABEL}
        </p>

        <div className="mt-4 space-y-1 text-sm text-ink-secondary">
          <div className="flex justify-between">
            <span>Quadra 1h</span>
            <span>{formatBRL(courtPrice)}</span>
          </div>
          <div className="flex justify-between">
            <span>Taxa de serviço</span>
            <span>{formatBRL(serviceFee)}</span>
          </div>
          <div className="flex justify-between border-t border-border/70 pt-2 font-semibold text-ink">
            <span>Total</span>
            <span>{formatBRL(totalAmount)}</span>
          </div>
        </div>
      </Card>

      <Card className="mt-4">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">
            Cotas · {formatBRL(quotaAmount)} cada
          </p>
          <Badge tone="accent">
            {paidCount} de {quotaCount} pagas
          </Badge>
        </div>

        <div className="mt-3">
          <ProgressBar value={paidCount} max={quotaCount} />
        </div>

        <div className="mt-4 divide-y divide-border/70">
          {participants.map((participant) => (
            <ParticipantRow key={participant.id} participant={participant} />
          ))}
          {isLoadingParticipants &&
            Array.from({ length: quotaCount - 1 }, (_, index) => <ParticipantRowSkeleton key={index} />)}
        </div>

        {canInvite && (
          <button
            type="button"
            onClick={async () => {
              const copied = await copyInviteLink(ROUTES.CHECKOUT);
              toast({
                title: copied ? 'Link de convite copiado' : 'Convite pronto',
                description: 'Quem entrar pelo link paga só a própria cota.',
              });
            }}
            className="mt-2 flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            + Convidar {participants.length + 1}º jogador
          </button>
        )}
      </Card>

      <div className="mt-6">
        <p className="mb-2 text-sm font-medium text-ink">Sua forma de pagamento</p>
        <PillTabs options={PAYMENT_METHODS} value={paymentMethod} onChange={setPaymentMethod} />
      </div>

      <div className="fixed inset-x-0 bottom-16 border-t border-border bg-surface px-4 py-4 shadow-card">
        <div className="mx-auto max-w-2xl">
          <Button className="w-full" onClick={pay} disabled={hasPaid || isPaying}>
            {hasPaid ? (
              <>
                <Check className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
                Cota paga
              </>
            ) : isPaying ? (
              'Processando...'
            ) : (
              `Pagar minha cota · ${formatBRL(quotaAmount)}`
            )}
          </Button>
        </div>
      </div>
    </section>
  );
}
